import { Scene } from '@babylonjs/core/scene';
import { eMaterialLocation, eRenderMode } from '@models/classes/enums';
import { ArrayMap } from '@models/classes/handlers';
import { Kb3dObject } from '@models/classes/kb3d-object';
import { MaterialNode } from '@models/classes/materials/material-node';
import { TextureLayer } from '@models/classes/materials/texture-layer';
import { SceneNode } from '@models/classes/scene-node';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces';
import { SyncService } from '@models/services/sync-service';
import { KbViewer } from '@view/kb-viewer';
import { AnnotationRenderer } from './annotation';
import { DimensionRenderer } from './dimension';
import { FeatureStackRenderer } from './feature-stack';
import { ConnectorRenderer } from './features';
import { SketchControlPointRenderer } from './features/sketch-control-point';
import { LightRenderer } from './light';
import { MateRenderer } from './mate';
import { MaterialRenderer } from './material';
import { MeshRenderer } from './mesh';
import { PathRenderer } from './path';
import { MateProcessor } from './processors/mate-processor';
import { Processor } from './processors/processor';
import { ReferenceProcessor } from './processors/reference-processor';
import { SketchProcessor } from './processors/sketch-processor';
import { MAX_RENDER_TIME, Renderer } from './renderer';
import { SceneRenderer } from './scene';
import { ShadowManager } from './shadow-manager';
import { TextureStackRenderer } from './texture-stack';
import { ViewpointRenderer } from './viewpoint';

let unprocessedChangesCount = 0;

export class ChangeMap extends ArrayMap<Token, ITrackedClass<Kb3dObject>> {}

export class RendererManager {
    deferredChanges: Array<ITrackedClass<Kb3dObject>> = [];
    maxRenderTime = MAX_RENDER_TIME;
    renderers: Set<Renderer<any, any>>;
    sceneRenderer: SceneRenderer;
    light: LightRenderer;
    material: MaterialRenderer;
    mesh: MeshRenderer;
    textures: TextureStackRenderer;
    viewpoint: ViewpointRenderer;
    features: FeatureStackRenderer;
    connector: ConnectorRenderer;
    controlPointRenderer: SketchControlPointRenderer;
    annotation: AnnotationRenderer;
    dimension: DimensionRenderer;
    mate: MateRenderer;
    path: PathRenderer;

    shadowManager: ShadowManager;
    processors = new Array<Processor>();
    mateProcessor: MateProcessor;
    referenceProcessor: ReferenceProcessor;
    sketchProcessor: SketchProcessor;

    private forcedRenderingMode: eRenderMode | undefined;

    getRenderMode() {
        return this.material.renderMode;
    }
    /** Do not use this function directly. Call setRenderMode on the viewer instead so the correct sceneNode is used. */
    setRenderMode(mode: eRenderMode, sceneNode: SceneNode, force?: boolean) {
        if (force) {
            this.forcedRenderingMode = mode;
        }
        this.material.renderMode = this.forcedRenderingMode || mode;
        this.mesh.setRenderMode(this.forcedRenderingMode || mode, sceneNode);
    }

    constructor(public viewer: KbViewer, public scene: Scene) {
        this.shadowManager = new ShadowManager(viewer.bbCache);
        this.sceneRenderer = new SceneRenderer(viewer, scene, viewer.gui, this);
        this.textures = new TextureStackRenderer(viewer, scene, this);
        this.material = new MaterialRenderer(viewer, scene, this.textures);
        this.light = new LightRenderer(viewer, scene, this.shadowManager);
        this.viewpoint = new ViewpointRenderer(viewer, scene);
        this.mesh = new MeshRenderer(viewer, scene, this, this.shadowManager);
        this.features = new FeatureStackRenderer(viewer, scene, this);
        this.connector = new ConnectorRenderer(viewer, scene, this);
        this.controlPointRenderer = new SketchControlPointRenderer(viewer, scene, this);
        this.annotation = new AnnotationRenderer(viewer, scene, this);
        this.dimension = new DimensionRenderer(viewer, scene, this);
        this.mate = new MateRenderer(viewer, scene);
        this.path = new PathRenderer(viewer, scene);

        // Order is important here, it defines the order in which renderers will be run
        this.renderers = new Set<Renderer<any, any>>([
            this.sceneRenderer,
            this.material,
            this.textures,
            this.viewpoint,
            this.path,
            this.mesh,
            this.light,
            this.features,
            this.connector,
            this.controlPointRenderer,
            this.annotation,
            this.dimension,
            this.mate,
        ]);

        this.referenceProcessor = new ReferenceProcessor(viewer);
        this.mateProcessor = new MateProcessor(
            viewer,
            scene,
            this.connector,
            this.controlPointRenderer,
            this.referenceProcessor
        );
        this.sketchProcessor = new SketchProcessor(this.controlPointRenderer);
        this.processors.push(this.mateProcessor);
        this.processors.push(this.referenceProcessor);
        this.processors.push(this.sketchProcessor);
    }

    isStable() {
        return (
            !SyncService.hasChanges &&
            this.features.queuedFeaturesToProcess.size === 0 &&
            this.textures.queuedTexturesToProcess.size === 0
        );
    }

    render() {
        let changes: ChangeMap;
        if (this.deferredChanges.length > 0) {
            changes = this.getChangeMap(this.deferredChanges);
        } else {
            for (const p of this.processors) {
                p.beforeRender();
            }
            // run deletions first
            const deletions = this.getChangeMap(SyncService.getDeletedNodes(), true);
            for (const p of this.processors) {
                p.beforeDeletions(deletions);
            }
            this.runChanges(deletions, (renderer, change) => renderer.delete3d(change));
            for (const p of this.processors) {
                p.afterDeletions(deletions);
            }
            //then run updates
            const newChanges = SyncService.getChangedNodes();
            changes = this.getChangeMap(newChanges);

            for (const p of this.processors) {
                p.beforeUpdates(changes);
            }
            changes = this.getChangeMap(SyncService.getChangedNodes()); //need to update changes in case processors added any
        }

        const allChangesByDid = new Map(
            changes.all().map(change => {
                return [change._dynamicId, change];
            })
        );
        const allChangeCount = allChangesByDid.size;
        const processedChangeMap = this.runChanges(changes, (renderer, change) => {
            renderer.update3d(change);
        });
        const processedChanges = processedChangeMap.all();
        processedChanges.forEach(change => {
            allChangesByDid.delete(change._dynamicId);
        });
        this.deferredChanges = Array.from(allChangesByDid.values());

        const skipCount = allChangeCount - processedChanges.length;
        // For now we are processing all changes becuase it causes unpredictable behavior.
        // The heaviest part of the render cycle is the geometry features processing.
        // if (processedChanges.length > 0 || skipCount > 0) {
        //     console.log('processed ' + processedChanges.length + ' changes, skipped ' + skipCount + ' changes');
        // }

        // Don't run the geometry and texture stack processing until all changes have been processed
        if (skipCount === 0) {
            this.features.update3dGeometry();
            this.textures.updateTextureStacks();
        }

        for (const p of this.processors) p.afterUpdates(processedChangeMap);
        for (const n of processedChanges) {
            n.commitChanges();
        }

        for (const p of this.processors) p.doAfterCleanup();

        // Output a console warning if there are a lot of changes not being processed, as this could indicate a problem with changes not being processed.
        const unchangedNodes = SyncService.getChangedNodes();

        if (unchangedNodes.length > 0) {
            unprocessedChangesCount++;
            if (unprocessedChangesCount > 1000) {
                unprocessedChangesCount = 0;
                console.warn('Scene stabilization is taking longer than expected.');
                for(const n of unchangedNodes) {
                    if(n._rootScene._dynamicId !== this.viewer.sceneNode._dynamicId) {
                        n.commitChanges();
                        console.warn(`node ${n.name} (${n.id}) is from a different, non-active scene instance. Clearing the change.`)
                    }
                }
            }
        }
    }

    /** runs a change map in order */
    protected runChanges(
        changes: ChangeMap,
        action: (renderer: Renderer<any, any>, change: ITrackedClass<Kb3dObject>) => void
    ) {
        const processedChanges = new ChangeMap();
        const renderStart = performance.now();

        for (const renderer of this.renderers) {
            const changeList = changes.get(renderer.modelToken);
            for (const change of changeList) {
                action(renderer, change);
                processedChanges.add(renderer.modelToken, change);
            }
        }

        const renderTime = performance.now() - renderStart;
        if (renderTime > 50) {
            console.warn(`Change cycle took ${renderTime}ms`);
        }

        return processedChanges;
    }
    protected getChangeMap(nodes: Array<ITrackedClass<Kb3dObject>>, includeOrphaned = false) {
        const changes = new ChangeMap();
        for (const n of nodes) {
            if (
                (n._rootScene?._dynamicId === this.viewer.sceneNode._dynamicId && n._level > -1) ||
                includeOrphaned ||
                this.isGlobalMaterialOrTexture(n) //global/library material nodes held outside of graph
            ) {
                const renderer = this.renderers.find(r => r.handlesNode(n));
                if (!renderer) throw 'unknown node type';
                changes.add(renderer.modelToken, n);
            } else if (n._rootScene == null) {
                // Clear changes on nodes that are not in any scene tree
                n.commitChanges();
            }
        }

        changes.get(Token.SpaceNode).sortBy(m => m._level); //sort meshes by level first

        return changes;
    }
    disposeAll() {
        this.shadowManager.dispose();
        for (const processor of this.processors) processor.dispose();
        for (const renderer of this.renderers) renderer.dispose();
    }

    protected isGlobalMaterialOrTexture(n: Kb3dObject) {
        let mat = n;
        if (n instanceof TextureLayer) mat = n._parent as Kb3dObject;
        return mat instanceof MaterialNode && mat.location != eMaterialLocation.local;
    }
}
